import { zodResolver } from '@hookform/resolvers/zod';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  OrganizationSOW,
  useOrganizationSOWs,
} from '../../../data-access/organizationSOWs';
import { SOWQueryFormSchema, SOWQueryInputs } from '../components/SOWQueryForm';

interface SOWQueryContextValue {
  searchTerm: string;
  filteredSOWs: OrganizationSOW[];
}

const SOWQueryContext = createContext<SOWQueryContextValue | undefined>(
  undefined
);

export const SOWQueryProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { sows } = useOrganizationSOWs();

  const sowQueryForm = useForm<SOWQueryInputs>({
    defaultValues: { searchTerm: '' },
    resolver: zodResolver(SOWQueryFormSchema),
  });

  const searchTerm = sowQueryForm.watch('searchTerm');

  const filteredSOWs = useMemo(
    () =>
      sows.filter((sow) =>
        sow.title.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [sows, searchTerm]
  );

  return (
    <SOWQueryContext.Provider
      value={{
        searchTerm,
        filteredSOWs,
      }}
    >
      <FormProvider {...sowQueryForm}>{children}</FormProvider>
    </SOWQueryContext.Provider>
  );
};

export const useSOWQuery = () => {
  const context = useContext(SOWQueryContext);
  if (context === undefined) {
    throw new Error('useSOWQuery must be used within a SOWQueryProvider');
  }
  return context;
};
